// Table with a mobile card fallback.
//
// At md (768px) and up it renders a real <table>. Below that it renders a
// list of cards, so a 390px phone never scrolls sideways. Only ONE of the
// two is in the DOM at a time (useIsDesktop), so screen readers and tests
// see each row once.
//
//   <DataTable
//     caption="Tenants"
//     rows={tenants}
//     rowKey={(t) => t.id}
//     rowHref={(t) => `/tenants/${t.id}/overview`}
//     columns={[{ id: 'name', header: 'Tenant', cell: (t) => t.name, primary: true }, …]}
//   />

import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/format'
import { useIsDesktop } from '@/lib/hooks'
import { ChevronRightIcon } from './icons'

export interface Column<T> {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  align?: 'left' | 'right'
  /** Width hint, e.g. "w-40". */
  className?: string
  /** The row's main cell: carries the row link, and heads the mobile card. */
  primary?: boolean
  /** Leave out of the mobile card. */
  hideOnMobile?: boolean
  /** Label in the mobile card (defaults to the header when it's a string). */
  mobileLabel?: string
}

export interface DataTableProps<T> {
  /** Accessible name for the table / list. */
  caption: string
  rows: T[]
  columns: Column<T>[]
  rowKey: (row: T) => string | number
  /** Makes each row a link (primary cell + whole-row click on desktop, whole card on mobile). */
  rowHref?: (row: T) => string
  /** Replace the default mobile card. */
  renderCard?: (row: T) => ReactNode
  /** Dim rows while a background refetch runs. */
  busy?: boolean
  className?: string
}

export function DataTable<T>({ caption, rows, columns, rowKey, rowHref, renderCard, busy, className }: DataTableProps<T>) {
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()
  const primary = columns.find((c) => c.primary) ?? columns[0]

  if (!isDesktop) {
    return (
      <ul aria-label={caption} aria-busy={busy || undefined} className={cn('divide-y divide-line', busy && 'opacity-60', className)}>
        {rows.map((row) => {
          const body = renderCard ? renderCard(row) : <DefaultCard row={row} columns={columns} primary={primary} />
          const href = rowHref?.(row)
          return (
            <li key={rowKey(row)}>
              {href ? (
                <Link to={href} className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-hover/60 active:bg-hover">
                  <div className="min-w-0 flex-1">{body}</div>
                  <ChevronRightIcon size={16} className="shrink-0 text-subtle" />
                </Link>
              ) : (
                <div className="px-4 py-3.5">{body}</div>
              )}
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    // relative: keeps sr-only (absolute) text inside the scroller instead of widening the page.
    <div className={cn('relative overflow-x-auto', className)}>
      <table aria-busy={busy || undefined} className={cn('w-full border-collapse text-left text-[13px]', busy && 'opacity-60')}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-line">
            {columns.map((c) => (
              <th
                key={c.id}
                scope="col"
                className={cn(
                  'whitespace-nowrap px-4 py-2.5 text-2xs font-bold uppercase tracking-label text-muted',
                  c.align === 'right' && 'text-right',
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const href = rowHref?.(row)
            return (
              <tr
                key={rowKey(row)}
                onClick={
                  href
                    ? (e) => {
                        // Let real links/buttons inside the row handle themselves.
                        if ((e.target as HTMLElement).closest('a, button, input, select, textarea, label')) return
                        if (window.getSelection?.()?.toString()) return
                        navigate(href)
                      }
                    : undefined
                }
                className={cn('border-b border-line last:border-b-0', href && 'cursor-pointer transition-colors hover:bg-hover/50')}
              >
                {columns.map((c) => (
                  <td key={c.id} className={cn('px-4 py-3 align-middle', c.align === 'right' && 'text-right', c.className)}>
                    {c === primary && href ? (
                      <Link to={href} className="rounded-sm text-ink hover:text-accent focus-visible:text-accent">
                        {c.cell(row)}
                      </Link>
                    ) : (
                      c.cell(row)
                    )}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function DefaultCard<T>({ row, columns, primary }: { row: T; columns: Column<T>[]; primary: Column<T> }) {
  const rest = columns.filter((c) => c !== primary && !c.hideOnMobile)
  return (
    <div className="grid gap-1.5">
      <div className="font-semibold text-ink">{primary.cell(row)}</div>
      {rest.length ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {rest.map((c) => (
            <div key={c.id} className="contents">
              <dt className="text-muted">{c.mobileLabel ?? (typeof c.header === 'string' ? c.header : c.id)}</dt>
              <dd className="min-w-0 text-ink">{c.cell(row)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  )
}
